import React, { useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Paper
} from '@mui/material';
import DocumentUploadSection from '../../../../../components/onboardingForms/DocumentUploadSection';

/**
 * Documents step for company onboarding
 */
export const DocumentsStep = ({ 
    companyTypes,
    documents,
    onDocumentChange,
    errors
}) => {
    // Define document requirements based on company types
    const documentRequirements = useMemo(() => {
        const requirements = [
            // Common documents for all companies
            {
                type: 'business_license',
                title: 'Business License',
                description: 'Upload your business license or registration document if available.',
                isRequired: false,
                acceptedFileTypes: ['.pdf', '.png', '.jpg', '.jpeg']
            },
            {
                type: 'insurance',
                title: 'Insurance Certificate',
                description: 'Upload your business insurance certificate if available.',
                isRequired: false,
                acceptedFileTypes: ['.pdf', '.png', '.jpg', '.jpeg']
            }
        ];
        
        // Add specific documents based on company types
        if (companyTypes.includes('Venue')) {
            requirements.push(
                {
                    type: 'venue_intake_form',
                    title: 'Client Intake Form',
                    description: 'Upload your venue client intake form if you have one.',
                    isRequired: false,
                    acceptedFileTypes: ['.pdf', '.doc', '.docx']
                }
            );
        }
        
        if (companyTypes.includes('Catering')) {
            requirements.push(
                {
                    type: 'catering_license',
                    title: 'Catering License',
                    description: 'Upload your food service or catering license if available.',
                    isRequired: false,
                    acceptedFileTypes: ['.pdf', '.png', '.jpg', '.jpeg']
                }
            );
        }
        
        if (companyTypes.includes('Sponsor')) {
            requirements.push(
                {
                    type: 'sponsor_branding',
                    title: 'Branding Guidelines',
                    description: 'Upload your branding guidelines and requirements if available.',
                    isRequired: false,
                    acceptedFileTypes: ['.pdf', '.doc', '.docx']
                }
            );
        }
        
        return requirements;
    }, [companyTypes]);

    // Handle file add
    const handleFileAdd = (documentType, files) => {
        onDocumentChange(documentType, 'add', files);
    };

    // Handle file remove
    const handleFileRemove = (documentType, fileIndex) => {
        onDocumentChange(documentType, 'remove', fileIndex);
    };

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
                Company Documents
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can upload any of the following documents related to your business. 
                While all documents are optional, providing them will help streamline your event planning process.
                Please upload only one file per document type.
            </Typography>
            
            {documentRequirements.map((requirement) => (
                <DocumentUploadSection
                    key={requirement.type}
                    title={requirement.title}
                    description={requirement.description}
                    isRequired={requirement.isRequired}
                    documentType={requirement.type}
                    acceptedFileTypes={requirement.acceptedFileTypes}
                    files={documents[requirement.type] || []}
                    onFileAdd={handleFileAdd}
                    onFileRemove={handleFileRemove}
                    error={errors?.documents?.[requirement.type]}
                    maxFiles={1}
                />
            ))}
        </Paper>
    );
}; 